import React, { createContext, useContext, useState, useEffect } from 'react';
import { Lernset } from '../util/types';
import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';

interface LernsetContextType {
    lernsets: Lernset[];
    addLernset: (name: string, description?: string) => Promise<string>;
    updateLernset: (id: string, updates: Partial<Lernset>) => Promise<void>;
    deleteLernset: (id: string) => Promise<void>;
    getLernsetById: (id: string) => Lernset | undefined;
}

const LearnsetContext = createContext<LernsetContextType | undefined>(undefined);

export const useLernsets = () => {
    const context = useContext(LearnsetContext);
    if (!context) throw new Error('useLernsets must be used within LernsetProvider');
    return context;
};

export const LernsetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [lernsets, setLernsets] = useState<Lernset[]>([]);
    const { currentUser } = useAuth();

    useEffect(() => {
        if (!currentUser) {
            setLernsets([]);
            return;
        }

        const fetchLernsets = async () => {
            const q = query(collection(db, 'lernsets'), where('userId', '==', currentUser.uid));
            const querySnapshot = await getDocs(q);
            const loaded: Lernset[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                loaded.push({
                    id: doc.id,
                    userId: data.userId,
                    name: data.name,
                    description: data.description,
                    createdAt: data.createdAt,
                    cards: data.cards || [],
                });
            });
            loaded.sort((a, b) => b.createdAt - a.createdAt);
            setLernsets(loaded);
        };

        fetchLernsets();
    }, [currentUser]);

    const addLernset = async (name: string, description = ''): Promise<string> => {
        if (!currentUser) throw new Error('Nicht eingeloggt');
        const newLernset = {
            userId: currentUser.uid,
            name,
            description,
            createdAt: Date.now(),
            cards: [],
        };
        const docRef = await addDoc(collection(db, 'lernsets'), newLernset);
        const createdLernset = { id: docRef.id, ...newLernset };
        setLernsets(prev => [createdLernset, ...prev]);
        return docRef.id;
    };

    const updateLernset = async (id: string, updates: Partial<Lernset>) => {
        if (!currentUser) throw new Error('Nicht eingeloggt');
        const docRef = doc(db, 'lernsets', id);
        const { userId, ...safeUpdates } = updates;
        await updateDoc(docRef, safeUpdates);
        setLernsets(prev =>
            prev.map(set => (set.id === id ? { ...set, ...safeUpdates } : set))
        );
    };

    const deleteLernset = async (id: string) => {
        if (!currentUser) throw new Error('Nicht eingeloggt');
        const docRef = doc(db, 'lernsets', id);
        await deleteDoc(docRef);
        setLernsets(prev => prev.filter(set => set.id !== id));
    };

    const getLernsetById = (id: string) => {
        return lernsets.find(set => set.id === id);
    };

    return (
        <LearnsetContext.Provider value={{ lernsets, addLernset, updateLernset, deleteLernset, getLernsetById }}>
            {children}
        </LearnsetContext.Provider>
    );
};